import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { DualPrice } from "@/components/DualPrice";
import { TripView } from "@/components/owner/TripView";

const CLIENT_STATUS = {
  pending_confirmation: {
    title: "Approved — thank you.",
    body: "Your concierge is now confirming every element with our private operators. You will hear from us the moment all is secured.",
  },
  sent_to_operator: {
    title: "Being confirmed with our operators.",
    body: "Every element of your journey is being locked in with our private operators as we speak.",
  },
  confirmed: {
    title: "Confirmed. Every detail is secured.",
    body: "Your journey is fully confirmed with our operators. See you in Iceland — your concierge remains one message away.",
  },
  changes_requested: {
    title: "Your adjustments are with your concierge.",
    body: "We are composing alternatives for the elements you set aside and will return shortly with a revised journey.",
  },
};

export default function TripReview() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState("");
  const [marks, setMarks] = useState({});
  const [daily, setDaily] = useState([]);
  const [wish, setWish] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [policyChecked, setPolicyChecked] = useState(false);
  const [policyName, setPolicyName] = useState("");

  const confirmPolicies = async () => {
    if (!policyChecked || !policyName.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/trips/${token}/policy-ack`, { name: policyName.trim() });
      setTrip((t) => ({ ...t, policy_ack: res.data.policy_ack }));
      toast.success("Thank you — cancellation policies confirmed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm right now.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/trips/${token}`)
      .then((res) => setTrip(res.data))
      .catch((e) => setError(e.response?.status === 403 ? "preparing" : "missing"));
    axios
      .get(`${API}/trips/${token}/daily`)
      .then((res) => setDaily(res.data.days || []))
      .catch(() => {});
  }, [token]);

  const reviewMode = trip?.status === "sent_to_client";
  const markCount = Object.keys(marks).length;

  const toggleMark = (itemId, action) =>
    setMarks((m) => {
      const next = { ...m };
      if (next[itemId]?.action === action) delete next[itemId];
      else next[itemId] = { action, note: next[itemId]?.note || "" };
      return next;
    });

  const setNote = (itemId, note) => setMarks((m) => ({ ...m, [itemId]: { ...m[itemId], note } }));

  const approveAll = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/trips/${token}/approve`);
      setTrip((t) => ({ ...t, status: "pending_confirmation" }));
      toast.success("Itinerary approved — your concierge has been notified");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendAdjustments = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/trips/${token}/adjustments`, {
        adjustments: Object.entries(marks).map(([item_id, v]) => ({ item_id, action: v.action, note: v.note })),
      });
      setTrip((t) => ({ ...t, status: "changes_requested" }));
      toast.success("Adjustments sent to your concierge");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send adjustments right now.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickRoom = async (hotelKey, optionId) => {
    try {
      const res = await axios.post(`${API}/trips/${token}/room-choice`, { hotel_key: hotelKey, option_id: optionId });
      setTrip((t) => ({
        ...t,
        hotels: (t.hotels || []).map((h, idx) =>
          (h.hotel_id || String(idx)) === hotelKey ? { ...h, picked_option: res.data.picked_option } : h
        ),
      }));
      toast.success(res.data.picked_option ? "Room preference sent to your concierge" : "Room preference cleared");
    } catch {
      toast.error("Could not save your choice right now.");
    }
  };

  const pickMenu = async (itemId, optionId) => {
    try {
      const res = await axios.post(`${API}/trips/${token}/menu-choice`, { item_id: itemId, option_id: optionId });
      setTrip((t) => ({
        ...t,
        items: (t.items || []).map((i) => (i.item_id === itemId ? { ...i, picked_option: res.data.picked_option } : i)),
      }));
      toast.success(res.data.picked_option ? "Restaurant choice sent to your concierge" : "Restaurant choice cleared");
    } catch {
      toast.error("Could not save your choice right now.");
    }
  };

  const acceptAlt = async (kind, key) => {
    try {
      await axios.post(`${API}/trips/${token}/accept-alternative`, { kind, key });
      setTrip((t) => ({
        ...t,
        items: (t.items || []).map((i) => (kind === "item" && i.item_id === key ? { ...i, full_alternative: "", alt_accepted: true } : i)),
        hotels: (t.hotels || []).map((h, idx) => (kind === "hotel" && (h.hotel_id || String(idx)) === key ? { ...h, full_alternative: "", alt_accepted: true } : h)),
      }));
      toast.success("Alternative accepted — your concierge has been notified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept right now.");
    }
  };

  const renderItemAction = (item) => {
    const mark = marks[item.item_id];
    return (
      <div className="border-t border-amber-500/10 pt-3">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid={`item-remove-${item.item_id}`}
            onClick={() => toggleMark(item.item_id, "removed")}
            className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
              mark?.action === "removed"
                ? "border-red-500 bg-red-500/15 text-red-300"
                : "border-neutral-700 text-neutral-400 hover:border-red-500/60 hover:text-red-300"
            }`}
          >
            <X className="h-3 w-3" /> Take this out
          </button>
          <button
            data-testid={`item-alternative-${item.item_id}`}
            onClick={() => toggleMark(item.item_id, "alternative")}
            className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
              mark?.action === "alternative"
                ? "border-amber-400 bg-amber-500/15 text-amber-200"
                : "border-neutral-700 text-neutral-400 hover:border-amber-400 hover:text-amber-200"
            }`}
          >
            Ask for an alternative
          </button>
        </div>
        {mark?.action === "alternative" && (
          <input
            data-testid={`item-note-${item.item_id}`}
            placeholder="What would you prefer instead? (optional)"
            value={mark.note}
            onChange={(e) => setNote(item.item_id, e.target.value)}
            className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-sm text-amber-100 placeholder:text-neutral-600 outline-none focus:border-amber-400"
          />
        )}
      </div>
    );
  };

  const statusInfo = trip ? CLIENT_STATUS[trip.status] : null;

  return (
    <div data-testid="trip-review-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <div className="flex justify-center">
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        </div>

        {error === "missing" && (
          <div data-testid="trip-not-found" className="mt-14 border border-dashed border-neutral-700 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-300">This journey has wandered off the map.</p>
            <p className="mt-3 text-sm text-neutral-500">The link may be incomplete — ask your concierge to resend it.</p>
          </div>
        )}
        {error === "preparing" && (
          <div data-testid="trip-preparing" className="mt-14 border border-dashed border-amber-500/30 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-200">Still being composed.</p>
            <p className="mt-3 text-sm text-neutral-500">Your concierge is putting the final touches on this journey. Check back shortly.</p>
          </div>
        )}
        {!error && !trip && (
          <div className="mt-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        )}

        {trip && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            {statusInfo && (
              <div
                data-testid="trip-status-banner"
                className={`mt-10 border p-6 text-center ${
                  trip.status === "confirmed"
                    ? "border-emerald-400/40 bg-emerald-500/10"
                    : "border-amber-500/30 bg-amber-500/5"
                }`}
              >
                <p className="font-serif text-2xl font-light text-neutral-50">{statusInfo.title}</p>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-400">{statusInfo.body}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  {["pending_confirmation", "sent_to_operator", "confirmed"].includes(trip.status) && (
                    <a
                      data-testid="trip-pdf-download"
                      href={`${API}/trips/${token}/pdf`}
                      className="inline-block border border-amber-500/50 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                    >
                      Download Your Itinerary (PDF)
                    </a>
                  )}
                  {trip.status === "confirmed" && (
                    <a
                      data-testid="trip-calendar-download"
                      href={`${API}/trips/${token}/calendar`}
                      className="inline-block border border-emerald-400/50 bg-emerald-500/10 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                    >
                      Save to Your Calendar
                    </a>
                  )}
                </div>
              </div>
            )}

            {trip.deposit_amount > 0 && ["pending_confirmation", "sent_to_operator", "confirmed"].includes(trip.status) && (
              <div
                data-testid="deposit-card"
                className={`mt-6 border p-6 text-center ${
                  trip.deposit_status === "paid"
                    ? "border-emerald-400/40 bg-emerald-500/10"
                    : "border-amber-500/40 bg-amber-500/5"
                }`}
              >
                {trip.deposit_status === "paid" ? (
                  <>
                    <p className="font-serif text-2xl font-light text-emerald-200">Deposit received — thank you.</p>
                    <p className="mt-2 text-sm text-neutral-400">Your journey is secured. We can't wait to host you.</p>
                  </>
                ) : (
                  <>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                      Deposit to secure your journey
                    </p>
                    <p data-testid="deposit-amount" className="mt-2 font-serif text-3xl font-light text-neutral-50">
                      <DualPrice value={`${Number(trip.deposit_amount)} ${(trip.deposit_currency || "usd").toUpperCase()}`} region={trip.client_region} />
                    </p>
                    {trip.deposit_link && (
                      <a
                        data-testid="pay-deposit-link"
                        href={trip.deposit_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-block bg-amber-500 px-8 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
                      >
                        Pay Deposit
                      </a>
                    )}
                    {trip.deposit_instructions && (
                      <p data-testid="deposit-instructions" className="mx-auto mt-4 max-w-md whitespace-pre-line text-sm leading-relaxed text-neutral-300">
                        {trip.deposit_instructions}
                      </p>
                    )}
                    <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                      Your concierge confirms receipt the moment it arrives
                    </p>
                  </>
                )}
              </div>
            )}

            {reviewMode && (
              <div className="mt-10 border border-amber-500/30 bg-amber-500/5 p-6 text-center">
                <p className="font-serif text-2xl font-light text-neutral-50">Your journey awaits your word.</p>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-400">
                  Approve it in full, or set aside anything that doesn't feel right — nothing is confirmed
                  until you say so.
                </p>
              </div>
            )}

            <div className="mt-10 border border-amber-500/20 bg-[#0c0c10]/80 p-6 sm:p-10">
              <TripView trip={trip} daily={daily} onRoomPick={pickRoom} onMenuPick={pickMenu} onAcceptAlt={acceptAlt} renderItemAction={reviewMode ? renderItemAction : undefined} />
            </div>

            {(() => {
              const deadlines = [
                ...(trip.hotels || []).filter((h) => h.cancel_by).map((h) => ({ label: h.name, date: h.cancel_by })),
                ...(trip.rentals || []).filter((r) => r.cancel_by).map((r) => ({ label: r.name, date: r.cancel_by })),
              ].sort((a, b) => a.date.localeCompare(b.date));
              if (!deadlines.length) return null;
              const ack = trip.policy_ack;
              const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
              return (
                <div data-testid="policy-section" className="mt-8 border border-amber-500/20 bg-[#0c0c10]/80 p-5 sm:p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">Cancellation policies</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                    Each element of your journey can be cancelled free of charge until the date shown — after that it is committed to:
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {deadlines.map((d, i) => (
                      <li key={i} data-testid={`policy-line-${i}`} className="flex items-baseline justify-between gap-4 border-b border-neutral-800/60 pb-1.5 text-sm">
                        <span className="text-neutral-200">{d.label}</span>
                        <span className="shrink-0 text-amber-200/90">{fmtDay(d.date)}</span>
                      </li>
                    ))}
                  </ul>
                  {ack ? (
                    <p data-testid="policy-confirmed" className="mt-4 flex items-center gap-2 text-sm text-emerald-300">
                      <Check className="h-4 w-4" />
                      Confirmed by {ack.name} · {new Date(ack.at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  ) : (
                    <div className="mt-4">
                      <label className="flex items-start gap-3 text-sm text-neutral-300">
                        <input
                          data-testid="policy-confirm-checkbox"
                          type="checkbox"
                          checked={policyChecked}
                          onChange={(e) => setPolicyChecked(e.target.checked)}
                          className="mt-1 h-4 w-4 accent-amber-500"
                        />
                        I understand and accept these cancellation policies
                      </label>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <input
                          data-testid="policy-name-input"
                          placeholder="Your name"
                          value={policyName}
                          onChange={(e) => setPolicyName(e.target.value)}
                          className="min-w-40 flex-1 border border-amber-900/40 bg-[#0d0d12] px-4 py-2.5 text-sm text-amber-100 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                        />
                        <button
                          data-testid="policy-confirm-button"
                          onClick={confirmPolicies}
                          disabled={submitting || !policyChecked || !policyName.trim()}
                          className="flex items-center gap-2 border border-amber-500/50 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-40"
                        >
                          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Confirm
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {reviewMode && (
              <div className="mt-8 flex flex-col items-center gap-4">
                <button
                  data-testid="approve-itinerary-button"
                  onClick={approveAll}
                  disabled={submitting}
                  className="flex w-full items-center justify-center gap-3 bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Approve the Entire Itinerary
                </button>
                <button
                  data-testid="send-adjustments-button"
                  onClick={sendAdjustments}
                  disabled={submitting || markCount === 0}
                  className="flex items-center gap-2 border border-neutral-600 px-6 py-3 text-xs uppercase tracking-[0.25em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Send className="h-3.5 w-3.5" />
                  Send My Adjustments {markCount > 0 ? `(${markCount})` : ""}
                </button>
              </div>
            )}

            <div className="mt-8 border border-amber-500/20 bg-[#0c0c10]/80 p-5 sm:p-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                Tell your concierge anything
              </p>
              <textarea
                data-testid="client-wish-input"
                rows={2}
                placeholder="Free-text wishes — 'can we swap the ATV tour for snowmobiles?' — your concierge turns it into changes"
                value={wish}
                onChange={(e) => setWish(e.target.value)}
                className="mt-3 w-full resize-none border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
              />
              <button
                data-testid="client-wish-send"
                onClick={async () => {
                  if (!wish.trim()) {
                    toast.error("Write your wish first.");
                    return;
                  }
                  setSubmitting(true);
                  try {
                    const res = await axios.post(`${API}/trips/${token}/wish`, { text: wish });
                    setTrip((t) => ({ ...t, status: res.data.trip_status }));
                    setWish("");
                    toast.success("Sent to your concierge");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not send right now.");
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                className="mt-3 flex items-center gap-2 border border-amber-500/50 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send to Concierge
              </button>

              {trip.client_wishes?.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-amber-500/10 pt-4">
                  {trip.client_wishes.map((w, i) => (
                    <div key={i} data-testid={`client-wish-${i}`} className="border border-neutral-800 p-3">
                      <p className="text-sm italic text-neutral-300">
                        &ldquo;{w.text}&rdquo;
                        {w.from && <span className="ml-2 text-[10px] not-italic uppercase tracking-[0.2em] text-amber-300/70">— {w.from}</span>}
                      </p>
                      {w.reply && (
                        <p data-testid={`client-wish-reply-${i}`} className="mt-2 border-l-2 border-amber-500/60 pl-3 text-sm text-amber-100/90">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">Your concierge</span>
                          <br />
                          {w.reply}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="mt-10 text-center font-serif text-sm italic text-amber-200/60">
              Questions at any hour — reply to your concierge's email and it lands on their desk.
            </p>
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <Link to="/" data-testid="trip-review-home-link" className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 transition-colors hover:text-amber-200">
            Exclusive Reykjavik · Key to the city — and beyond
          </Link>
        </div>
      </div>
    </div>
  );
}
